import { useState } from "react"
import { Link } from "react-router-dom"
import Card from "@mui/material/Card"
import IconButton from "@mui/material/IconButton"
import InputAdornment from "@mui/material/InputAdornment"
import Visibility from "@mui/icons-material/Visibility"
import VisibilityOff from "@mui/icons-material/VisibilityOff"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"
import defaultLogo from "assets/images/logo.svg"
import useBrandLogo from "hooks/useBrandLogo"
import useBackgroundImage from "hooks/useBackgroundImage"
import BasicLayout from "layouts/authentication/components/BasicLayout"
import defaultBgImage from "assets/images/background.webp"
import config from "config"
import { withBasename } from "appBasePath"
import { jwtDecode } from "jwt-decode"
import { invalidateAllImageCaches } from "utils/remoteImageCache"
import packageInfo from "../../../../package.json"
import useLocalStorage from "useLocalStorage"
import CircularProgress from "@mui/material/CircularProgress"
import { Backdrop } from "@mui/material"

function Basic() {
  const brandLogo = useBrandLogo(defaultLogo)
  const bgImage = useBackgroundImage(defaultBgImage)
  const [usuario, setUsuario] = useState("")
  const [, setToken] = useLocalStorage("token", "")
  const [, setRole] = useLocalStorage("role", "")
  const [, setUsername] = useLocalStorage("username", "")
  const [, setCompany] = useLocalStorage("company", "")
  const [, setCompanyId] = useLocalStorage("companyId", "")
  const [clave, setClave] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [showPassword, setShowPassword] = useState(false)


  const getRoleFromToken = (token) => {
    try {
      const decoded = jwtDecode(token)
      return decoded.role
    } catch (error) {
      console.error("Invalid token", error)
      return null
    }
  }

  const getUsernameFromToken = (token) => {
    try {
      const decoded = jwtDecode(token)
      return decoded.sub
    } catch (error) {
      console.error("Invalid token", error)
      return null
    }
  }

  const getCompanyFromToken = (token) => {
    try {
      const decoded = jwtDecode(token)
      return decoded.company || ""   // si no existe, devuelve vacío
    } catch (error) {
      console.error("Invalid token", error)
      return ""
    }
  }

  const getCompanyIdFromToken = (token) => {
    try {
      const decoded = jwtDecode(token)
      return decoded.companyId || ""   // si no existe, devuelve vacío
    } catch (error) {
      console.error("Invalid token", error)
      return ""
    }
  }

  const handleLogin = async (event) => {
    if (event) event.preventDefault()
    if (loading) return
    setError("")
    setLoading(true)
    const payload = { usuario, clave }

    try {
      const response = await fetch(`${config.api.baseURL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          accept: "application/json",
        },
        body: JSON.stringify(payload),
      })
      console.log(response)
      if (response.ok) {
        console.log("ok")
        const data = await response.json()
        setToken(data.jwt)
        const role = getRoleFromToken(data.jwt)
        console.log("User role:", role)
        const username = getUsernameFromToken(data.jwt)
        const company = getCompanyFromToken(data.jwt)
        const companyId = getCompanyIdFromToken(data.jwt)
        console.log("Company:", role)
        invalidateAllImageCaches()
        setCompany(company)
        setCompanyId(companyId)
        setRole(role)
        setUsername(username)
        // Limpiar cualquier sesión embed residual en sessionStorage para que
        // App.js no confunda el token embed con el nuevo token de login normal.
        sessionStorage.removeItem("token")
        sessionStorage.removeItem("role")
        sessionStorage.removeItem("username")
        sessionStorage.removeItem("company")
        sessionStorage.removeItem("companyId")
        sessionStorage.removeItem("embed.session.active")
        sessionStorage.removeItem("embed.session.apiKey")
        window.location.href = withBasename("/")
      } else {
        console.log("error")
        const errorData = await response.json()
        setError(errorData.message || "Login failed. Please try again.")
      }
    } catch (error) {
      setError(
        "Ocurrió un error. No se ha podido conectar con el servidor. Verifique su conexión y reintente."
      )
      console.error("Error during login:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (event) => {
    if (loading) return
    if (event.key === "Enter") {
      handleLogin(event)
    }
  }
  let textColor = "white"

  return (
    <BasicLayout image={bgImage}>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" size={70} thickness={4} />
      </Backdrop>
      <MDBox display="flex" justifyContent="center" alignItems="center" mt={10} mb={10}>
        <Card style={{ width: "600px", border: "1px solid #DAD9D9" }}>
          <MDBox
            variant="gradient"
            bgColor="primary"
            borderRadius="lg"
            coloredShadow="info"
            mx={2}
            mt={-3}
            p={2}
            mb={1}
            textAlign="center"
          >
            <MDBox
              component="img"
              src={brandLogo}
              alt="logo"
              width={{ xs: "50%", lg: "30%", xl: "30%", sm: "45%", md: "45%" }}
              mt={1}
            />
            <MDTypography
              sx={{
                fontSize: {
                  xs: "1.5rem",
                  sm: "2rem",
                  md: "1.5rem",
                  lg: "2rem",
                },
              }}
              fontWeight="medium"
              color="white"
              mt={1}
            >
              Portal de monitoreo
            </MDTypography>
            <MDTypography variant="h6" fontWeight="medium" color="white" mt={1}>
              Iniciar Sesión
            </MDTypography>
            <MDTypography
              component="p"
              variant="caption"
              fontWeight="bold"
              color={textColor}
              sx={{ mt: 1 }}
              opacity={2}
            >
              {config.versionName}
            </MDTypography>
            <MDTypography
              component="p"
              variant="caption"
              fontWeight="light"
              color={textColor}
              sx={{ mt: 1 }}
              opacity={2}
            >
              versión:{packageInfo.version}
            </MDTypography>
          </MDBox>
          <MDBox pt={4} pb={3} px={3}>
            <MDBox component="form" role="form" onSubmit={handleLogin} onKeyDown={handleKeyDown} aria-busy={loading}>
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  label="Usuario"
                  fullWidth
                  value={usuario}
                  onChange={(e) => setUsuario(e.target.value)}
                  disabled={loading}
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type={showPassword ? "text" : "password"}
                  label="Contraseña"
                  fullWidth
                  value={clave}
                  onChange={(e) => setClave(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          color="text"
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  disabled={loading}
                />
              </MDBox>
              <MDBox mb={1} textAlign="right">
                <MDTypography
                  component={Link}
                  to="/authentication/reset-password/cover"
                  variant="caption"
                  color="primary"
                  fontWeight="medium"
                  style={{ textDecoration: "underline" }}
                >
                  Restablecer Contraseña
                </MDTypography>
              </MDBox>
              <MDBox mt={3} mb={1}>
                <MDButton variant="gradient" color="primary" fullWidth type="submit" disabled={loading || !usuario || !clave}>
                  Ingresar
                </MDButton>
              </MDBox>
              {error && (
                <MDBox mt={2} textAlign="center">
                  <MDTypography variant="caption" color="error">
                    {error}
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </BasicLayout>
  )
}

export default Basic


